import { Box, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import type { EngagementBreakdownItem } from "./use-engagement-breakdown-summary";
import { useEngagementBreakdownSummary } from "./use-engagement-breakdown-summary";

const fallbackBreakdown: EngagementBreakdownItem[] = [
  { clicked: 0, delivered: 0, label: "", percentage: 0 },
];

export const EngagementBreakdownData: FC<{
  breakdown: EngagementBreakdownItem[];
  engagementPercentage?: number;
}> = ({ breakdown, engagementPercentage }) => {
  const summaryBreakdown = breakdown.length > 0 ? breakdown : fallbackBreakdown;
  const { sortedBreakdown } = useEngagementBreakdownSummary(summaryBreakdown);

  if (sortedBreakdown.length === 0) return null;

  const mostEngaged = sortedBreakdown[0]!;
  const leastEngaged = sortedBreakdown[sortedBreakdown.length - 1] ?? mostEngaged;

  return (
    <Stack spacing={3}>
      <EngagementWithThisSend engagementPercentage={engagementPercentage} />
      <Stack direction="row" spacing={3}>
        <EngagementSummary
          title="Most engaged"
          item={mostEngaged}
          backgroundColor="#E7F8F6"
          textColor="#084A42"
        />
        <EngagementSummary
          title="Least engaged"
          item={leastEngaged}
          backgroundColor="#FFF7E9"
          textColor="#66460D"
        />
      </Stack>
      <EngagementBreakdownChart
        data={sortedBreakdown}
        engagementPercentage={engagementPercentage}
      />
    </Stack>
  );
};

const EngagementWithThisSend: FC<{
  engagementPercentage?: number;
}> = ({ engagementPercentage }) => (
  <Box
    sx={{
      bgcolor: "background.paper",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      boxShadow: 1,
      px: 3,
      py: 2,
      textAlign: "center",
    }}
  >
    <Typography
      variant="overline"
      sx={{
        color: "text.secondary",
        fontWeight: 600,
        textTransform: "uppercase",
      }}
    >
      Engagement with this send
    </Typography>
    <Typography variant="h5" sx={{ color: "text.primary", fontWeight: 600, lineHeight: 1.3 }}>
      {engagementPercentage !== undefined ? `${engagementPercentage}%` : "—"}
    </Typography>
    <Typography variant="caption" sx={{ fontWeight: 400, lineHeight: 1.6 }}>
      Engagement is calculated by dividing the number of people who clicked a link in the
      Communication by the number of people who received it.
    </Typography>
  </Box>
);

const EngagementSummary: FC<{
  title: string;
  item: EngagementBreakdownItem;
  backgroundColor: string;
  textColor: string;
}> = ({ title, item, backgroundColor, textColor }) => (
  <Box sx={{ flex: 1, backgroundColor, borderRadius: 1, px: 3, py: 2 }}>
    <Stack spacing={1}>
      <Typography
        variant="overline"
        sx={{ color: textColor, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase" }}
      >
        {title}
      </Typography>
      <Typography variant="body1" sx={{ color: textColor }}>
        <Box component="span" sx={{ fontWeight: 700 }}>
          {item.label}
        </Box>{" "}
        {item.clicked}/{item.delivered} ({item.percentage}%)
      </Typography>
    </Stack>
  </Box>
);

const EngagementBreakdownChart: FC<{
  data: EngagementBreakdownItem[];
  engagementPercentage?: number;
}> = ({ data, engagementPercentage }) => (
  <Box sx={{ position: "relative", pt: 3 }}>
    {engagementPercentage !== undefined && (
      <>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            left: `${engagementPercentage}%`,
            position: "absolute",
            top: 0,
            transform: "translateX(-50%)",
            whiteSpace: "nowrap",
          }}
        >
          This send ({engagementPercentage}%)
        </Typography>
        <Box
          sx={{
            borderLeft: "2px dashed #5048E5",
            bottom: 0,
            left: `${engagementPercentage}%`,
            position: "absolute",
            top: 26,
            zIndex: 1,
          }}
        />
      </>
    )}
    <Stack spacing={1}>
      {data.map((item) => (
        <Box
          key={item.label}
          sx={{
            bgcolor: "#C2E5C4",
            borderRadius: 1,
            height: 44,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              bgcolor: "#7BC67E",
              borderRadius: 1,
              height: "100%",
              width: `${item.percentage}%`,
            }}
          />
          <Typography
            variant="body1"
            sx={{
              color: "#084A42",
              fontWeight: 400,
              left: 1.5,
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              pl: 2,
            }}
          >
            <Box component="span" sx={{ fontWeight: 700 }}>
              {item.label}
            </Box>{" "}
            {item.clicked}/{item.delivered} ({item.percentage}%)
          </Typography>
        </Box>
      ))}
    </Stack>
  </Box>
);
